import React, { useCallback, useRef, useState } from 'react';
import { AnalyticsContext, useTracking } from '@/lib/analyticsEvents';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import ForumIcon from '../common/ForumIcon';
import LWPopper from '../common/LWPopper';
import LWClickAwayListener from '../common/LWClickAwayListener';
import DropdownMenu from '../dropdowns/DropdownMenu';
import DropdownItem from '../dropdowns/DropdownItem';
import { Paper } from '../widgets/Paper';
import { useCurrentUser } from '../common/withUser';
import { useDialog } from '../common/withDialog';
import LoginPopup from '../users/LoginPopup';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles('CommentsSubmitDropdown', (theme: ThemeType) => ({
  buttonWrapper: {
    borderTopRightRadius: theme.borderRadius.default,
    borderBottomRightRadius: theme.borderRadius.default,
    display: 'flex',
  },
  divider: {
    opacity: 0.9,
    flex: 1,
    marginTop: 'auto',
    marginBottom: 'auto',
    height: "calc(100% - 12px)"
  },
  button: {
    borderRadius: theme.borderRadius.default,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    padding: '6px 2px',
    minWidth: 0,
    boxShadow: 'none',
    color: theme.palette.lwTertiary.main,
    "&:hover": {
      opacity: 0.5,
    },
  },
  dropdownIcon: {
    transform: 'translateY(1px)'
  },
  popper: {
    marginTop: 6
  },
  dropdownMenu: {
    backgroundColor: theme.palette.dropdown.background,
    borderRadius: theme.borderRadius.small,
  },
  dropdownItem: {
    padding: '4px 8px'
  }
}));

export const CommentsSubmitDropdown = ({handleSubmit}: {
  handleSubmit: (meta: {draft: boolean}) => Promise<void>,
}) => {
  const classes = useStyles(styles);
  const { captureEvent } = useTracking();
  const currentUser = useCurrentUser();
  const { openDialog } = useDialog();

  const [menuOpen, innerSetMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const setMenuOpen = useCallback((open: boolean) => {
    captureEvent("menuToggled", { openAfter: open, pageElementContext: "CommentsSubmitDropdown" });
    innerSetMenuOpen(open);
  }, [captureEvent, innerSetMenuOpen])

  return (
    <AnalyticsContext pageElementContext="CommentsSubmitDropdown">
      <div ref={dropdownRef} className={classes.buttonWrapper}>
        <div className={classes.divider} />
        <Button
          className={classes.button}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <ForumIcon className={classes.dropdownIcon} icon="ThickChevronDown" />
        </Button>
      </div>
      <LWPopper
        open={menuOpen}
        anchorEl={dropdownRef.current}
        placement="bottom-end"
        className={classes.popper}
      >
        <LWClickAwayListener onClickAway={() => setMenuOpen(false)}>
          <Paper>
            <DropdownMenu className={classes.dropdownMenu}>
              <DropdownItem
                title={"Save As Draft"}
                menuItemClassName={classes.dropdownItem}
                onClick={() => {
                  if (!currentUser) {
                    openDialog({
                      name: "LoginPopup",
                      contents: ({onClose}) => <LoginPopup onClose={onClose}/>
                    });
                    return;
                  }

                  void handleSubmit({ draft: true });
                  setMenuOpen(false);
                }}
              />
            </DropdownMenu>
          </Paper>
        </LWClickAwayListener>
      </LWPopper>
    </AnalyticsContext>
  );
}

export default CommentsSubmitDropdown;
